import React, { useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot, Brush
} from 'recharts';
import { TrendingUp, AlertTriangle } from 'lucide-react';

const CHART_COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4',
    '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b',
];

const shortDate = (d) => (d || '').substring(0, 5);

// ============================================
// Кастомный Tooltip
// ============================================
const AnomalyTooltip = ({ active, payload, label, anomalyMap }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <div className="bg-slate-900 border border-slate-600 rounded-lg p-3 shadow-xl text-xs max-w-xs">
            <div className="text-slate-400 font-semibold mb-2 border-b border-slate-700 pb-1">
                📅 {label}
            </div>
            {payload.map((entry, idx) => {
                const anomaly = anomalyMap[`${entry.dataKey}|${label}`];
                return (
                    <div key={idx} className="mb-1.5">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: entry.stroke }}
                            />
                            <span className="text-slate-300">{entry.name}:</span>
                            <span className="text-white font-mono font-semibold">
                                {entry.value != null ? entry.value.toFixed(3) : 'НС'}
                            </span>
                        </div>
                        {anomaly && (
                            <div className={`mt-1 px-2 py-1 rounded text-[11px] border ${anomaly.type === 'spike'
                                    ? 'bg-red-500/10 border-red-500/30 text-red-300'
                                    : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                                }`}>
                                <div className="font-semibold">
                                    {anomaly.type === 'spike' ? '⚡ Скачок' : '📉 Провал'}
                                    {' '}{anomaly.changePercent > 0 ? '+' : ''}{anomaly.changePercent}%
                                </div>
                                <div className="text-slate-400 mt-0.5">
                                    Пред. сутки ({anomaly.previousDate}): {anomaly.previousValue}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ============================================
// Основной компонент
// ============================================
const AnomalyChart = ({ title, data, dataKeys, anomalies = [], unit = '' }) => {
    const anomalyMap = useMemo(() => {
        const map = {};
        anomalies.forEach(a => {
            const key = `${a.paramName}|${shortDate(a.date)}`;
            map[key] = a;
        });
        return map;
    }, [anomalies]);

    // ✅ Расширенные периоды: ±1 день от границ аномалии
    const anomalyPeriods = useMemo(() => {
        const dates = data.map(d => d.date); // ["01.07", "02.07", ...]
        const seen = new Set();
        const periods = [];

        anomalies.forEach(a => {
            const x1Raw = shortDate(a.previousDate);
            const x2Raw = shortDate(a.date);
            const key = `${x1Raw}-${x2Raw}-${a.type}`;
            if (seen.has(key)) return;
            seen.add(key);

            // Расширяем на ±1 точку для видимости
            const idx1 = dates.indexOf(x1Raw);
            const idx2 = dates.indexOf(x2Raw);

            const x1 = idx1 > 0 ? dates[idx1 - 1] : x1Raw;
            const x2 = idx2 < dates.length - 1 ? dates[idx2 + 1] : x2Raw;

            periods.push({ x1, x2, type: a.type, date: x2Raw });
        });
        return periods;
    }, [anomalies, data]);

    // ✅ Вертикальные линии в точках аномалий
    const anomalyLines = useMemo(() => {
        const seen = new Set();
        return anomalies
            .filter(a => dataKeys.some(k => k.key === a.paramName))
            .filter(a => {
                const key = `${shortDate(a.date)}-${a.type}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map(a => ({
                x: shortDate(a.date),
                type: a.type,
                changePercent: a.changePercent,
            }));
    }, [anomalies, dataKeys]);

    // ✅ Точки для подписей %
    const anomalyDots = useMemo(() => {
        return anomalies
            .filter(a => dataKeys.some(k => k.key === a.paramName))
            .map(a => {
                const row = data.find(d => d.date === shortDate(a.date));
                return row ? { ...a, y: row[a.paramName] } : null;
            })
            .filter(Boolean);
    }, [anomalies, data, dataKeys]);

    if (!data || data.length === 0) {
        return (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-2" />
                Нет данных для отображения
            </div>
        );
    }

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {/* Заголовок */}
            <div className="px-4 py-3 bg-slate-700/50 border-b border-slate-700 flex items-center justify-between">
                <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-red-400" />
                    {title}
                    {unit && <span className="text-xs text-slate-400 font-normal">({unit})</span>}
                </h4>
                {anomalies.length > 0 && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/15 border border-red-500/30 rounded-lg text-red-300 text-xs font-medium">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Аномалий: {anomalies.length}
                    </span>
                )}
            </div>

            {/* Легенда */}
            {anomalies.length > 0 && (
                <div className="px-4 py-2 bg-slate-700/20 border-b border-slate-700/50 flex flex-wrap items-center gap-4 text-[11px] text-slate-400">
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-500 border-2 border-red-300 inline-block" />
                        Скачок (рост)
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-blue-500 border-2 border-blue-300 inline-block" />
                        Провал (падение)
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-6 h-3 bg-red-500/25 border border-red-400 rounded-sm inline-block" />
                        Период аномалии
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-0.5 h-4 bg-red-400 inline-block" />
                        Линия аномалии
                    </span>
                </div>
            )}

            {/* График */}
            <div className="p-4">
                <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis
                            dataKey="date"
                            stroke="#94a3b8"
                            tick={{ fontSize: 10 }}
                            interval="preserveStartEnd"
                        />
                        <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} />

                        <Tooltip content={<AnomalyTooltip anomalyMap={anomalyMap} />} />
                        <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />

                        {/* ✅ 1. ЯРКИЕ затенённые полосы — периоды аномалий */}
                        {anomalyPeriods.map((period, idx) => (
                            <ReferenceArea
                                key={`area-${idx}`}
                                x1={period.x1}
                                x2={period.x2}
                                fill={period.type === 'spike' ? '#ef4444' : '#3b82f6'}
                                fillOpacity={0.18}
                                stroke={period.type === 'spike' ? '#ef4444' : '#3b82f6'}
                                strokeOpacity={0.7}
                                strokeWidth={1.5}
                                ifOverflow="extendDomain"
                            />
                        ))}

                        {/* ✅ 2. Вертикальные линии в точках аномалий */}
                        {anomalyLines.map((line, idx) => (
                            <ReferenceLine
                                key={`vline-${idx}`}
                                x={line.x}
                                stroke={line.type === 'spike' ? '#ef4444' : '#3b82f6'}
                                strokeWidth={2}
                                strokeOpacity={0.8}
                                strokeDasharray="4 2"
                            />
                        ))}

                        {/* Линии параметров */}
                        {dataKeys.map((key, idx) => {
                            const color = CHART_COLORS[idx % CHART_COLORS.length];
                            return (
                                <Line
                                    key={key.key}
                                    type="monotone"
                                    dataKey={key.key}
                                    name={key.name}
                                    stroke={color}
                                    strokeWidth={2}
                                    dot={(props) => {
                                        const { cx, cy, payload, dataKey: lineKey } = props;
                                        if (cx == null || cy == null) {
                                            return <g key={`dot-${lineKey}-${payload?.date}`} />;
                                        }

                                        const anomaly = anomalyMap[`${lineKey}|${payload?.date}`];
                                        if (anomaly) {
                                            const isSpike = anomaly.type === 'spike';
                                            return (
                                                <g key={`dot-${lineKey}-${payload.date}`}>
                                                    {/* Внешний ореол */}
                                                    <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={12}
                                                        fill={isSpike ? '#ef4444' : '#3b82f6'}
                                                        fillOpacity={0.25}
                                                    />
                                                    {/* Среднее кольцо */}
                                                    <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={7}
                                                        fill={isSpike ? '#ef4444' : '#3b82f6'}
                                                        fillOpacity={0.5}
                                                    />
                                                    {/* Ядро */}
                                                    <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={4}
                                                        fill={isSpike ? '#ef4444' : '#3b82f6'}
                                                        stroke="#fff"
                                                        strokeWidth={2}
                                                    />
                                                </g>
                                            );
                                        }
                                        return (
                                            <circle
                                                key={`dot-${lineKey}-${payload?.date}`}
                                                cx={cx}
                                                cy={cy}
                                                r={2}
                                                fill={color}
                                                stroke="none"
                                            />
                                        );
                                    }}
                                    activeDot={{ r: 5, strokeWidth: 2 }}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            );
                        })}


                        <Brush
                            dataKey="date"
                            height={20}
                            stroke="#06b6d4"
                            fill="#1e293b"
                            travellerWidth={8}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default AnomalyChart;